import { useAuthStore } from "@/stores/authStore";

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/v1/admin`
  : "/api/v1/admin";

/**
 * Error thrown when a request fails (non-2xx response).
 * Carries the HTTP status and any message extracted from the response body so
 * callers can surface a meaningful, diagnosable message instead of silently
 * treating an error body as success.
 */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = useAuthStore.getState().token;

  const headers: HeadersInit = {
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData
  if (!(options.body instanceof FormData)) {
    (headers as Record<string, string>)["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    useAuthStore.getState().logout();
    // Preserve where the user was so we can return them after re-login.
    const current = window.location.pathname + window.location.search;
    const next = current && current !== "/login" ? `?next=${encodeURIComponent(current)}` : "";
    window.location.href = `/login${next}`;
    throw new ApiError("登入已過期，請重新登入", 401);
  }

  return response;
}

/**
 * Extract a human-readable error message from a failed response body.
 */
async function extractErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.clone().json();
    if (data && typeof data === "object") {
      const msg = (data as Record<string, unknown>).error ?? (data as Record<string, unknown>).message;
      if (typeof msg === "string" && msg.trim()) return msg;
    }
  } catch {
    // Body was not JSON — fall through to the default message.
  }
  return fallback;
}

/**
 * Parse a JSON response, throwing an ApiError (with the backend message) when
 * the response is not ok. This prevents error bodies from being mistaken for
 * successful data.
 */
async function parseJson<T>(response: Response, fallbackMessage = "請求失敗"): Promise<T> {
  if (!response.ok) {
    throw new ApiError(await extractErrorMessage(response, fallbackMessage), response.status);
  }
  return response.json() as Promise<T>;
}

// OfficialStyle types
export interface OfficialStyle {
  id: string;
  name: string;
  description: string | null;
  triggerScene: string;
  prompt: string;
  referenceImages: string[];
  category: string;
  tags: string[];
  status: "DRAFT" | "REVIEW" | "PUBLISHED" | "ARCHIVED";
  isEnabled: boolean;
  sortOrder: number;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface CreateOfficialStyleInput {
  name: string;
  description?: string;
  triggerScene: string;
  prompt: string;
  referenceImages?: string[];
  category: string;
  tags?: string[];
  status?: "DRAFT" | "REVIEW" | "PUBLISHED" | "ARCHIVED";
  isEnabled?: boolean;
  sortOrder?: number;
}

export type UpdateOfficialStyleInput = Partial<CreateOfficialStyleInput>;

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BatchProcessJob {
  id: string;
  status: "pending" | "processing" | "completed" | "failed";
  totalImages: number;
  processedImages: number;
  currentPhase?: "analyzing" | "generating" | "done";
  results: BatchProcessResult[];
  createdAt: string;
  completedAt: string | null;
}

export interface BatchProcessResult {
  imageUrl: string;
  generatedImageUrl?: string; // AI-generated showcase image
  name: string;
  triggerScene: string;
  prompt: string;
  category: string;
  tags: string[];
  status: "success" | "failed";
  error?: string;
}

export interface GeneratedImageResponse {
  success: boolean;
  data?: {
    url: string;
    r2Key: string;
  };
  error?: string;
}

export interface GenerateStatusResponse {
  available: boolean;
  model: string;
}

export interface PromptOptimizationResponse {
  success: boolean;
  data?: {
    optimizedPrompt: string;
    improvements: string[];
    suggestions: string[];
  };
  error?: string;
}

export interface ImageAnalysisResult {
  name: string;
  triggerScene: string;
  prompt: string;
  category: string;
  tags: string[];
}

export interface ImageAnalysisResponse {
  success: boolean;
  data?: ImageAnalysisResult;
  error?: string;
}

// AppRelease types
export interface AppRelease {
  id: string;
  version: string;
  platform: string;
  critical: boolean;
  notes: string | null;
  pubDate: string;
  signature: string;
  r2ObjectKey: string;
  fileSizeBytes: string | null;
  active: boolean;
  createdAt: string;
}

export interface ReleaseStats {
  totalReleases: number;
  activeReleases: number;
  platforms: string[];
  latestVersion: string | null;
}

// API functions
export const api = {
  // Official Styles
  getStyles: async (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    search?: string;
    category?: string;
  }): Promise<PaginatedResponse<OfficialStyle>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
    if (params?.status) searchParams.set("status", params.status);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.category) searchParams.set("category", params.category);

    const response = await fetchWithAuth(`/styles?${searchParams.toString()}`);
    return response.json();
  },

  getStyle: async (id: string): Promise<OfficialStyle> => {
    const response = await fetchWithAuth(`/styles/${id}`);
    return response.json();
  },

  createStyle: async (data: CreateOfficialStyleInput): Promise<OfficialStyle> => {
    const response = await fetchWithAuth("/styles", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return parseJson<OfficialStyle>(response, "建立失敗，請重試");
  },

  updateStyle: async (id: string, data: UpdateOfficialStyleInput): Promise<OfficialStyle> => {
    const response = await fetchWithAuth(`/styles/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return parseJson<OfficialStyle>(response, "儲存失敗，請重試");
  },

  deleteStyle: async (id: string): Promise<void> => {
    const response = await fetchWithAuth(`/styles/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      throw new ApiError(await extractErrorMessage(response, "刪除失敗，請重試"), response.status);
    }
  },

  // Image upload
  uploadImage: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth("/upload", {
      method: "POST",
      body: formData,
    });
    return response.json();
  },

  uploadImages: async (files: File[]): Promise<{ urls: string[] }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await fetchWithAuth("/upload/batch", {
      method: "POST",
      body: formData,
    });
    return response.json();
  },

  // Batch processing
  startBatchProcess: async (data: {
    imageUrls: string[];
    defaultCategory?: string;
    autoGenerateTriggerScene?: boolean;
    autoGeneratePrompt?: boolean;
    autoGenerateTags?: boolean;
    autoGenerateShowcaseImage?: boolean;
  }): Promise<{ jobId: string }> => {
    const response = await fetchWithAuth("/batch/start", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getBatchJobStatus: async (jobId: string): Promise<BatchProcessJob> => {
    const response = await fetchWithAuth(`/batch/${jobId}`);
    return response.json();
  },

  confirmBatchResults: async (
    jobId: string,
    confirmedStyles: CreateOfficialStyleInput[]
  ): Promise<{ created: number }> => {
    const response = await fetchWithAuth(`/batch/${jobId}/confirm`, {
      method: "POST",
      body: JSON.stringify({ styles: confirmedStyles }),
    });
    return response.json();
  },

  // Categories
  getCategories: async (): Promise<string[]> => {
    const response = await fetchWithAuth("/categories");
    return response.json();
  },

  // Image Generation
  getGenerateStatus: async (): Promise<GenerateStatusResponse> => {
    const response = await fetchWithAuth("/generate/status");
    return response.json();
  },

  generateImage: async (data: {
    prompt: string;
    width?: number;
    height?: number;
    steps?: number;
    oldImageUrl?: string; // If provided, old image will be deleted
  }): Promise<GeneratedImageResponse> => {
    const response = await fetchWithAuth("/generate/image", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.json();
  },

  generateShowcaseImage: async (data: {
    stylePrompt: string;
    styleName: string;
    oldImageUrl?: string; // If provided, old image will be deleted
  }): Promise<GeneratedImageResponse> => {
    const response = await fetchWithAuth("/generate/showcase", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteGeneratedImage: async (imageUrl: string): Promise<{ success: boolean; deleted: boolean }> => {
    const response = await fetchWithAuth("/generate/image", {
      method: "DELETE",
      body: JSON.stringify({ imageUrl }),
    });
    return response.json();
  },

  optimizePrompt: async (data: {
    prompt: string;
    styleName?: string;
    triggerScene?: string;
  }): Promise<PromptOptimizationResponse> => {
    const response = await fetchWithAuth("/generate/optimize-prompt", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.json();
  },

  analyzeImage: async (imageBase64: string): Promise<ImageAnalysisResponse> => {
    const response = await fetchWithAuth("/generate/analyze-image", {
      method: "POST",
      body: JSON.stringify({ imageBase64 }),
    });
    return response.json();
  },

  // App Releases
  getReleases: async (params?: {
    page?: number;
    pageSize?: number;
    platform?: string;
    version?: string;
  }): Promise<PaginatedResponse<AppRelease>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
    if (params?.platform) searchParams.set("platform", params.platform);
    if (params?.version) searchParams.set("version", params.version);

    const response = await fetchWithAuth(`/releases?${searchParams.toString()}`);
    return response.json();
  },

  getRelease: async (id: string): Promise<AppRelease> => {
    const response = await fetchWithAuth(`/releases/${id}`);
    return response.json();
  },

  getReleaseVersions: async (): Promise<string[]> => {
    const response = await fetchWithAuth("/releases/versions");
    return response.json();
  },

  getReleaseDownloadUrl: async (id: string): Promise<{ url: string; expiresIn: number }> => {
    const response = await fetchWithAuth(`/releases/${id}/download-url`);
    return response.json();
  },

  updateRelease: async (
    id: string,
    data: { active?: boolean; critical?: boolean; notes?: string }
  ): Promise<AppRelease> => {
    const response = await fetchWithAuth(`/releases/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return parseJson<AppRelease>(response, "更新版本狀態失敗，請重試");
  },

  getReleaseStats: async (): Promise<ReleaseStats> => {
    const response = await fetchWithAuth("/releases/stats/summary");
    return response.json();
  },
};
